/**
 * Shared rack list helpers — grouping and table cell display (#995 / #1052)
 */

import { DEFAULT_RACK_CONFIGURATION } from '../../types/rack';
import {
  buildRepeatableNameSubline,
  filterRepeatableLocationTagTableColumns,
  formatRepeatableLocationTagColumnHeader,
  isNativeCompactLayout,
  repeatableNameColumnUsesDoubleLine,
  type RepeatableLocationTagColumnOptions,
} from '../../utils/locationTagListDisplay';
import {
  generateAutoRackName,
  isAutoGeneratedRackName,
} from '../../utils/rackAutoName';

import type {
  RackConfiguration,
  GroupedRackConfigurations,
  UpdateRackConfigurationInput,
} from '../../types/rack';

/** Desktop web columns (#995 / Aman). Platform is the rack form factor; there is
 * intentionally no MODEL column — PDU model lives in the rack editor, not the list. */
export const RACK_TABLE_DESKTOP_COLUMNS = [
  'NAME',
  'PLATFORM',
  'QTY',
  'MAX KW',
  'RPDU / PLUG',
  'ROOM',
] as const;

/** Injected only when `showStatusColumn` is set (Devices view). */
export const RACK_TABLE_STATUS_COLUMN = 'STATUS' as const;

/**
 * Compact mirrors desktop column order; QTY folds into NAME subline (double-line mobile).
 * ROOM (#999 Story 3.4) replaces RPDU / PLUG on compact so the location tag stays visible.
 * Desktop: NAME | PLATFORM | QTY | MAX KW | RPDU / PLUG | ROOM
 * Compact: NAME (+Qty) | PLATFORM (+use) | MAX KW | ROOM
 */
export const RACK_TABLE_COMPACT_COLUMNS = [
  'NAME',
  'PLATFORM',
  'MAX KW',
  'ROOM',
] as const;

/** All column keys (superset for typing and legacy imports). */
export const RACK_TABLE_COLUMNS = RACK_TABLE_DESKTOP_COLUMNS;

export type RackTableLayout = 'desktop' | 'compact';

export type RackTableColumnKey =
  | (typeof RACK_TABLE_DESKTOP_COLUMNS)[number]
  | (typeof RACK_TABLE_COMPACT_COLUMNS)[number]
  | typeof RACK_TABLE_STATUS_COLUMN;

export const RACK_TABLE_COLUMN_ALIGN: Record<
  RackTableColumnKey,
  'left' | 'center'
> = {
  NAME: 'left',
  STATUS: 'left',
  PLATFORM: 'left',
  QTY: 'left',
  'MAX KW': 'left',
  'RPDU / PLUG': 'left',
  ROOM: 'left',
};

export function rackTableColumnsForLayout(
  layout: RackTableLayout,
  options?: RepeatableLocationTagColumnOptions,
): readonly RackTableColumnKey[] {
  const columns =
    layout === 'compact'
      ? RACK_TABLE_COMPACT_COLUMNS
      : RACK_TABLE_DESKTOP_COLUMNS;
  return filterRepeatableLocationTagTableColumns(columns, options, layout);
}

export function rackTableHeaderLabel(
  column: RackTableColumnKey,
  layout: RackTableLayout,
): string {
  if (layout === 'compact') {
    if (column === 'RPDU / PLUG') {
      return formatRepeatableLocationTagColumnHeader(column, 'RPDU');
    }
    if (column === 'MAX KW') {
      return formatRepeatableLocationTagColumnHeader(column, 'KW');
    }
  }
  return formatRepeatableLocationTagColumnHeader(column, column);
}

export function normalizeRackStatus(
  status: RackConfiguration['rackStatus'] | string | undefined,
): RackConfiguration['rackStatus'] {
  return status === 'existing' ? 'existing' : 'new';
}

export function groupRacksByStatus(
  racks: RackConfiguration[],
): GroupedRackConfigurations {
  return {
    existing: racks.filter(
      rack => normalizeRackStatus(rack.rackStatus) === 'existing',
    ),
    new: racks.filter(rack => normalizeRackStatus(rack.rackStatus) === 'new'),
  };
}

export type RackListSection = 'existing' | 'new';

export function sortRacksByDisplayOrder(
  racks: RackConfiguration[],
): RackConfiguration[] {
  return [...racks].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
  );
}

/** Reorder racks within EXISTING or NEW section; reassigns global displayOrder (#1100). */
export function applyRackSectionReorder(
  sortedRacks: RackConfiguration[],
  section: RackListSection,
  fromIndex: number,
  toIndex: number,
): RackConfiguration[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return sortedRacks;
  }

  const grouped = groupRacksByStatus(sortedRacks);
  const existing = sortRacksByDisplayOrder(grouped.existing);
  const newRacks = sortRacksByDisplayOrder(grouped.new);
  const sectionList = section === 'existing' ? existing : newRacks;

  if (
    fromIndex >= sectionList.length ||
    toIndex >= sectionList.length ||
    sectionList.length < 2
  ) {
    return sortedRacks;
  }

  const reorderedSection = [...sectionList];
  const [moved] = reorderedSection.splice(fromIndex, 1);
  reorderedSection.splice(toIndex, 0, moved);

  const merged =
    section === 'existing'
      ? [...reorderedSection, ...newRacks]
      : [...existing, ...reorderedSection];

  return merged.map((rack, index) => ({
    ...rack,
    displayOrder: index + 1,
  }));
}

/** Merge editor save payload onto a session rack without wiping fields with `undefined`. */
export function applyRackSessionPatch(
  rack: RackConfiguration,
  patch: UpdateRackConfigurationInput,
): RackConfiguration {
  const merged: RackConfiguration = { ...rack };
  (Object.keys(patch) as Array<keyof UpdateRackConfigurationInput>).forEach(
    key => {
      const value = patch[key];
      if (value !== undefined) {
        (merged as Record<string, unknown>)[key] = value;
      }
    },
  );
  merged.rackStatus = normalizeRackStatus(merged.rackStatus);
  return merged;
}

export function rackIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  if (left == null || right == null) {
    return false;
  }
  return Number(left) === Number(right);
}

export function getRackPlatformDisplay(rack: RackConfiguration): string {
  return rack.rackPlatform?.trim() || '—';
}

export function getRackUseDisplay(rack: RackConfiguration): string {
  return rack.rackUse?.trim() || '—';
}

/** PDU model column — manufacturer moves to subline when both are set. */
export function getRackPduModelDisplay(rack: RackConfiguration): string {
  return rack.rackPduModel?.trim() || '—';
}

/** NAME column primary — user-defined rack name, else "Rack N" by display order. */
export function getRackNamePrimaryDisplay(rack: RackConfiguration): string {
  const name = rack.rackName?.trim();
  if (name) {
    return name;
  }
  if (rack.displayOrder != null && rack.displayOrder > 0) {
    return `Rack ${rack.displayOrder}`;
  }
  return '—';
}

/** Editor chrome title — user rack name, else "Rack N", else "New rack". */
export function getRackEditChromeTitle(
  rackName: string | undefined,
  options: {
    isNew?: boolean;
    displayOrder?: number;
    positionIndex?: number;
  } = {},
): string {
  const trimmed = rackName?.trim();
  if (trimmed) {
    return trimmed;
  }
  if (options.isNew) {
    return 'New rack';
  }
  const position =
    options.positionIndex != null
      ? options.positionIndex + 1
      : options.displayOrder;
  if (position != null && position > 0) {
    return `Rack ${position}`;
  }
  return 'New rack';
}

export function formatRackTableCell(
  rack: RackConfiguration,
  column: RackTableColumnKey,
): string {
  switch (column) {
    case 'NAME':
      return getRackNamePrimaryDisplay(rack);
    case 'PLATFORM':
      return getRackPlatformDisplay(rack);
    case 'QTY':
      return rack.rackQuantity != null && Number.isFinite(rack.rackQuantity)
        ? String(rack.rackQuantity)
        : '—';
    case 'MAX KW':
      return rack.rackPduMaxKwPerRack?.trim() || '—';
    case 'RPDU / PLUG':
      return rack.rackPduCountPerRack?.trim() || '—';
    case 'ROOM':
      // Rendered via LocationRoomCell in the row (needs tag-name lookup); text unused.
      return '—';
    case 'STATUS':
      // Rendered via RepeatableUnitStatusPill in the row.
      return '';
    default:
      return '—';
  }
}

/** MODEL subline — manufacturer when PDU model is set. */
export function getRackModelSubline(
  rack: RackConfiguration,
): string | undefined {
  const model = rack.rackPduModel?.trim();
  const manufacturer = rack.rackPduManufacturer?.trim();
  if (model && manufacturer) {
    return manufacturer;
  }
  return undefined;
}

/** MODEL subline — manufacturer when PDU model is set. */
export function getRackModelSublineForList(
  rack: RackConfiguration,
): string | undefined {
  return getRackModelSubline(rack);
}

/** PLATFORM subline — rack use. */
export function getRackPlatformSubline(
  rack: RackConfiguration,
): string | undefined {
  const rackUse = rack.rackUse?.trim();
  return rackUse || undefined;
}

/** RPDU / PLUG subline — input plug type. */
export function getRackPlugSubline(
  rack: RackConfiguration,
): string | undefined {
  const plug = rack.rackPduInputPlugType?.trim();
  return plug || undefined;
}

/** QTY subline under NAME on mobile-web compact (unchanged — no label prefix). */
export function getRackQuantitySubline(
  rack: RackConfiguration,
): string | undefined {
  if (rack.rackQuantity != null && Number.isFinite(rack.rackQuantity)) {
    return `Qty ${rack.rackQuantity}`;
  }
  return undefined;
}

/**
 * QTY subline for the native compact trailing (Max kW) cell — labeled `Qty:` and
 * relocated out of NAME per Option B (#999). Native only; mobile web keeps
 * {@link getRackQuantitySubline}.
 */
export function getRackQuantityNativeCompactSubline(
  rack: RackConfiguration,
): string | undefined {
  if (rack.rackQuantity != null && Number.isFinite(rack.rackQuantity)) {
    return `Qty: ${rack.rackQuantity}`;
  }
  return undefined;
}

/** NAME subline — qty (compact) and/or facility location tag (#995 / #999). */
export function getRackNameSubline(
  rack: RackConfiguration,
  layout: RackTableLayout,
  locationTagName?: string | null,
): string | undefined {
  return buildRepeatableNameSubline(layout, {
    compactParts:
      layout === 'compact' ? [getRackQuantitySubline(rack)] : undefined,
    locationTagName,
  });
}

export function getRackColumnSubline(
  column: RackTableColumnKey,
  rack: RackConfiguration,
  layout: RackTableLayout,
  options?: { locationTagName?: string | null },
): string | undefined {
  // Native compact (#999 Option B): STATUS pill folds under NAME (rendered by the
  // row), the Qty fold relocates to the trailing MAX KW cell, and the descriptive
  // PLATFORM subline carries a `Use:` label.
  if (isNativeCompactLayout(layout)) {
    if (column === 'PLATFORM') {
      const use = rack.rackUse?.trim();
      return use ? `Use: ${use}` : undefined;
    }
    if (column === 'MAX KW') {
      return getRackQuantityNativeCompactSubline(rack);
    }
    return undefined;
  }
  if (column === 'NAME') {
    return getRackNameSubline(rack, layout, options?.locationTagName);
  }
  if (column === 'PLATFORM') {
    return getRackPlatformSubline(rack);
  }
  if (column === 'RPDU / PLUG') {
    return getRackPlugSubline(rack);
  }
  return undefined;
}

export function rackColumnUsesDoubleLine(
  column: RackTableColumnKey,
  layout: RackTableLayout = 'desktop',
  options?: { locationTagName?: string | null },
): boolean {
  if (isNativeCompactLayout(layout)) {
    // NAME is single-line (the pill renders beneath it in the row); the middle
    // (PLATFORM) and trailing (MAX KW) cells double-line their relocated sublines.
    return column === 'PLATFORM' || column === 'MAX KW';
  }
  if (column === 'NAME') {
    return repeatableNameColumnUsesDoubleLine(layout, options?.locationTagName);
  }
  return column === 'PLATFORM' || column === 'RPDU / PLUG';
}

/**
 * Primary list title — user-defined rack name preferred, then manufacturer + PDU model,
 * then rack platform fallback, then "Rack N" when displayOrder is available.
 * Returns null only when no suitable name can be determined.
 */
export function getRackDisplayName(rack: RackConfiguration): string | null {
  // Prefer user-defined rack name first (#995 Rack Naming)
  const rackName = rack.rackName?.trim();
  if (rackName) {
    return rackName;
  }

  // Fall back to manufacturer + PDU model
  const titled = [rack.rackPduManufacturer, rack.rackPduModel]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (titled) {
    return titled;
  }

  // Fall back to rack platform
  const platform = rack.rackPlatform?.trim();
  if (platform) {
    return platform;
  }

  // Final fallback to "Rack N" based on displayOrder
  if (rack.displayOrder != null && rack.displayOrder > 0) {
    return `Rack ${rack.displayOrder}`;
  }

  return null;
}

export type RackPreviewChipKey =
  | 'maxKw'
  | 'model'
  | 'location'
  | 'locationInRack';

export interface RackLabeledPreviewChip {
  key: RackPreviewChipKey;
  label: string;
  value: string | null;
}

/** Labeled preview chips for list cards (desktop mockup — #1076). */
export function getRackLabeledPreviewChips(
  rack: RackConfiguration,
  options?: { locationTagName?: string | null },
): RackLabeledPreviewChip[] {
  const maxKw = rack.rackPduMaxKwPerRack?.trim() || null;
  const model = rack.rackPduModel?.trim() || null;
  const location = options?.locationTagName?.trim() || null;
  const locationInRack = rack.rackPduLocation?.trim() || null;
  return [
    { key: 'maxKw', label: 'Max KW', value: maxKw },
    { key: 'model', label: 'Model', value: model },
    { key: 'location', label: 'Location tag', value: location },
    {
      key: 'locationInRack',
      label: 'Location in Rack',
      value: locationInRack,
    },
  ];
}

/** @deprecated Use getRackLabeledPreviewChips for list UI. */
export function getRackPreviewChips(
  rack: RackConfiguration,
): Array<string | null> {
  return getRackLabeledPreviewChips(rack).map(chip => chip.value);
}

function normalizeRackNameKey(name: string | undefined): string | null {
  const trimmed = name?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/** Unique duplicate name — user names get `-copy`; unnamed/auto names get a fresh serial. */
export function buildDuplicateRackName(
  sourceName: string | undefined,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): string {
  const trimmed = sourceName?.trim();

  if (trimmed && !isAutoGeneratedRackName(trimmed)) {
    const taken = new Set(
      [...takenNames]
        .map(normalizeRackNameKey)
        .filter((name): name is string => name != null),
    );

    let candidate = `${trimmed}-copy`;
    if (!taken.has(candidate.toLowerCase())) {
      return candidate;
    }

    let suffix = 2;
    while (taken.has(`${trimmed}-copy-${suffix}`.toLowerCase())) {
      suffix += 1;
    }
    return `${trimmed}-copy-${suffix}`;
  }

  return generateAutoRackName(tempId, takenNames);
}

/** Assign a serial rack name when the user did not provide one. */
export function ensureRackNameForCreate(
  data: UpdateRackConfigurationInput,
  tempId: number,
  takenNames: Iterable<string | undefined> = [],
): UpdateRackConfigurationInput {
  if (data.rackName?.trim()) {
    return data;
  }
  return {
    ...data,
    rackName: generateAutoRackName(tempId, takenNames),
  };
}

/** Client-side uniqueness check before persisting rack session changes. */
export function validateSessionRackNames(
  session: RackConfiguration[],
): string | null {
  const seen = new Map<string, string>();

  for (const rack of session) {
    const trimmed = rack.rackName?.trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      return `Rack name "${trimmed}" is already used by another rack in this request. Rack names must be unique.`;
    }
    seen.set(key, trimmed);
  }

  return null;
}

/** Clone rack fields for duplicate flow (no id/metadata; keeps source existing/new status). */
export function cloneRackForCreate(
  source: RackConfiguration,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): Partial<RackConfiguration> {
  const {
    id: _id,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    requestId,
    rackStatus,
    rackPduAdditionalInfo: _rackPduAdditionalInfo,
    rackName: sourceRackName,
    ...fields
  } = source;
  const duplicateName = buildDuplicateRackName(
    sourceRackName,
    takenNames,
    tempId,
  );
  return {
    ...fields,
    requestId,
    rackStatus:
      rackStatus === 'existing' || rackStatus === 'new' ? rackStatus : 'new',
    rackName: duplicateName,
  };
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export function parseOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function createDraftRackConfiguration(
  data: UpdateRackConfigurationInput,
  displayOrder: number,
  tempId: number,
): RackConfiguration {
  return {
    ...DEFAULT_RACK_CONFIGURATION,
    ...data,
    id: tempId,
    displayOrder: data.displayOrder ?? displayOrder,
    rackStatus: normalizeRackStatus(data.rackStatus),
  } as RackConfiguration;
}

/** Client-only id for a rack synthesized from legacy flat request columns. */
export const LEGACY_RACK_SYNTHETIC_ID = -1;

/** Whether a list row may offer delete (draft rows or persisted id > 0, not legacy synthetic). */
export function canDeleteRackFromList(
  rack: RackConfiguration,
  isDraftMode: boolean,
): boolean {
  if (rack.id == null) {
    return false;
  }
  if (isDraftMode) {
    return true;
  }
  return rack.id > 0 && rack.id !== LEGACY_RACK_SYNTHETIC_ID;
}

const LEGACY_RACK_SOURCE_KEYS = [
  'rackPlatform',
  'rackQuantity',
  'rackDescription',
  'rackUse',
  'rackPduManufacturer',
  'rackPduModel',
  'rackPduMountingType',
  'rackDimensionsHeight',
  'rackDimensionsWidth',
  'rackDimensionsLength',
  'rackPduMaxKwPerRack',
  'rackPduPowerType',
  'rackPduCountPerRack',
  'rackPduInputPlugType',
] as const;

export function hasLegacyRackRequestFields(
  source: Record<string, unknown>,
): boolean {
  return LEGACY_RACK_SOURCE_KEYS.some(key => {
    const value = source[key];
    if (value == null) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    return true;
  });
}

/** Build one display row from pre–multi-rack flat request fields when child rows are absent. */
export function buildLegacyRackFromRequestFields(
  source: Record<string, unknown>,
  requestId: string | number,
): RackConfiguration | null {
  if (!hasLegacyRackRequestFields(source)) {
    return null;
  }

  const numericId =
    typeof requestId === 'number' ? requestId : parseInt(String(requestId), 10);

  return {
    id: LEGACY_RACK_SYNTHETIC_ID,
    requestId: Number.isFinite(numericId) ? numericId : 0,
    rackStatus: 'existing',
    displayOrder: 1,
    rackPlatform: source.rackPlatform as string | undefined,
    rackQuantity: source.rackQuantity as number | undefined,
    rackDescription: source.rackDescription as string | undefined,
    rackUse: source.rackUse as string | undefined,
    rackDimensionsHeight: source.rackDimensionsHeight as number | undefined,
    rackDimensionsWidth: source.rackDimensionsWidth as number | undefined,
    rackDimensionsLength: source.rackDimensionsLength as number | undefined,
    rackPduMountingType: source.rackPduMountingType as string | undefined,
    rackPduCountPerRack: source.rackPduCountPerRack as string | undefined,
    rackPduOutletSwitchingMonitored: source.rackPduOutletSwitchingMonitored as
      | string
      | undefined,
    rackPduOutletTypesQuantity: source.rackPduOutletTypesQuantity as
      | string
      | undefined,
    rackPduPowerType: source.rackPduPowerType as string | undefined,
    rackPduMaxKwPerRack: source.rackPduMaxKwPerRack as string | undefined,
    rackPduAdditionalInfo: source.rackPduAdditionalInfo as string | undefined,
    rackPduManufacturer: source.rackPduManufacturer as string | undefined,
    rackPduModel: source.rackPduModel as string | undefined,
    rackPduInputPlugType: source.rackPduInputPlugType as string | undefined,
    rackPduDcPowerShelves: source.rackPduDcPowerShelves as number | undefined,
    rackPduCapacityAmps: source.rackPduCapacityAmps as number | undefined,
    rackPduCapacityVolts: source.rackPduCapacityVolts as number | undefined,
    rackPduCapacityKw: source.rackPduCapacityKw as number | undefined,
    rackPduLoadReadingsKw: source.rackPduLoadReadingsKw as string | undefined,
    rackPduLocation: source.rackPduLocation as string | undefined,
  };
}
